import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import dayjs, { Dayjs } from 'dayjs'
import { Button } from 'designSystem/components/Button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import { Text } from 'designSystem/components/Text'
import { TimeRange } from 'features/systemLens'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { TimeSeriesService } from 'features/timeSeries/timeSeriesService'
import {
  Activity,
  Calendar,
  CalendarDays,
  Clock1,
  Clock8,
  Database,
  FileStack,
  SunMedium,
  TrendingUp,
} from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ChartTimeUnit } from 'types'

import { CustomRangePicker } from './CustomRangePicker'
import { TimeRangeDisplay } from './TimeRangeDisplay'

const RangeLayout = styled.button`
  height: auto;
  flex-wrap: wrap;
  gap: 0 0.375rem;
  padding: 0.125rem 0.25rem;

  @container (max-width: 400px) {
    [data-role='start-date'],
    [data-role='end-date'] {
      width: 100%;
      text-align: center;
    }

    [data-role='separator'],
    [data-role='duration'] {
      display: none;
    }
  }
`

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;

  @media (max-width: 479px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

interface PredefinedRange {
  label: MessageDescriptor
  value: TimeRange['preset']
  icon: React.ReactNode
  getRange: () => [dayjs.Dayjs, dayjs.Dayjs]
  relativeLookback?: {
    value: number
    unit: ChartTimeUnit
  }
}

const predefinedRanges: PredefinedRange[] = [
  {
    label: msg`Last Hour`,
    value: 'last-hour',
    icon: <Clock1 />,
    getRange: () => [dayjs().subtract(1, 'hour'), dayjs()],
    relativeLookback: { value: 1, unit: ChartTimeUnit.HOURS },
  },
  {
    label: msg`Last 8 Hours`,
    value: 'last-8-hours',
    icon: <Clock8 />,
    getRange: () => [dayjs().subtract(8, 'hours'), dayjs()],
    relativeLookback: { value: 8, unit: ChartTimeUnit.HOURS },
  },
  {
    label: msg`Last 24 Hours`,
    value: 'last-24-hours',
    icon: <SunMedium />,
    getRange: () => [dayjs().subtract(24, 'hours'), dayjs()],
    relativeLookback: { value: 24, unit: ChartTimeUnit.HOURS },
  },
  {
    label: msg`Last 7 Days`,
    value: 'last-7-days',
    icon: <Activity />,
    getRange: () => [dayjs().subtract(7, 'days'), dayjs()],
    relativeLookback: { value: 7, unit: ChartTimeUnit.DAYS },
  },
  {
    label: msg`Last 30 Days`,
    value: 'last-30-days',
    icon: <TrendingUp />,
    getRange: () => [dayjs().subtract(30, 'days'), dayjs()],
    relativeLookback: { value: 30, unit: ChartTimeUnit.DAYS },
  },
  {
    label: msg`Last 3 Months`,
    value: 'last-3-months',
    icon: <FileStack />,
    getRange: () => [dayjs().subtract(3, 'months'), dayjs()],
    relativeLookback: { value: 90, unit: ChartTimeUnit.DAYS },
  },
  {
    label: msg`Last Year`,
    value: 'last-year',
    icon: <Calendar />,
    getRange: () => [dayjs().subtract(1, 'year'), dayjs()],
    relativeLookback: { value: 365, unit: ChartTimeUnit.DAYS },
  },
  {
    label: msg`Last 5 Years`,
    value: 'last-5-years',
    icon: <Database />,
    getRange: () => [dayjs().subtract(5, 'years'), dayjs()],
    relativeLookback: { value: 365 * 5, unit: ChartTimeUnit.DAYS },
  },
  {
    label: msg`YTD`,
    value: 'ytd',
    icon: <CalendarDays />,
    getRange: () => [dayjs().startOf('year'), dayjs()],
  },
]

interface ActiveTimeRangeSectionWithPopoverProps {
  isCustom: boolean
  onClick: () => void
  fromTime: dayjs.Dayjs
  toTime: dayjs.Dayjs
  timeRange: TimeRange
  onRangeClick?: () => void
  showDateConfig?: boolean
  onOpenChange?: (open: boolean) => void
  onClose?: () => void
}

export const ActiveTimeRangeSectionWithPopover: React.FC<
  ActiveTimeRangeSectionWithPopoverProps
> = ({
  isCustom,
  onClick,
  fromTime,
  toTime,
  timeRange,
  onRangeClick,
  showDateConfig = false,
  onOpenChange,
  onClose,
}) => {
  const { i18n } = useLingui()

  const {
    setTimeRange,
    liveTail,
    setLiveTail,
    dateMode,
    setDateMode,
    relativeStart,
    setRelativeStart,
    relativeEnd,
    setRelativeEnd,
    selectedSensors,
  } = useScatterplotContext()
  const [selectedPreset, setSelectedPreset] = useState<PredefinedRange | null>(
    null
  )

  useEffect(() => {
    if (dateMode === 'relative') {
      const presetByValue = predefinedRanges.find(
        (range) =>
          range.relativeLookback?.value === relativeStart.value &&
          range.relativeLookback?.unit === relativeStart.unit
      )
      setSelectedPreset(presetByValue ?? null)
    } else if (dateMode === 'absolute') {
      if (timeRange.from === dayjs().startOf('year').toISOString()) {
        const ytdPreset = predefinedRanges.find(
          (range) => range.value === 'ytd'
        )
        setSelectedPreset(ytdPreset ?? null)
      } else setSelectedPreset(null)
    }
  }, [fromTime, toTime, dateMode, relativeStart, relativeEnd, timeRange])

  const handlePresetSelect = (range: PredefinedRange) => {
    setSelectedPreset(range)
    const [from, to] = range.getRange()

    if (range.relativeLookback) {
      setDateMode('relative')
      setRelativeStart(range.relativeLookback)
      setRelativeEnd({ isNow: true, value: 0, unit: ChartTimeUnit.DAYS })
      setLiveTail(true)
    } else {
      setDateMode('absolute')
      setLiveTail(false)
    }

    // Bake a pen-aware interval for the NEW span — carrying the previous
    // window's interval over (e.g. 1-minute buckets onto a 5-year range)
    // bypasses the bucket budget entirely.
    const { interval, interval_unit } =
      TimeSeriesService.getIntervalForPenCount(
        Math.max(1, to.diff(from, 'seconds')),
        selectedSensors.length
      )
    setTimeRange({
      from: from.toISOString(),
      to: to.toISOString(),
      preset: range.value,
      interval,
      intervalUnit: interval_unit,
    })

    if (onClose) {
      onClose()
    }
  }

  const rangeSectionContent = (
    <Button
      asChild
      type='button'
      variant='ghost'
      pressed='accent'
      aria-pressed={isCustom}
      onClick={onRangeClick ? onClick : undefined}>
      <RangeLayout>
        <TimeRangeDisplay
          fromTime={fromTime}
          toTime={toTime}
          liveTail={liveTail}
          timeRange={timeRange}
        />
      </RangeLayout>
    </Button>
  )

  if (onRangeClick) {
    return rangeSectionContent
  }

  const handleCustomRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      setSelectedPreset(null)
      setDateMode('absolute')
      setLiveTail(false)
      const { interval, interval_unit } =
        TimeSeriesService.getIntervalForPenCount(
          Math.max(1, dates[1].diff(dates[0], 'seconds')),
          selectedSensors.length
        )
      setTimeRange({
        from: dates[0].toISOString(),
        to: dates[1].toISOString(),
        preset: 'custom',
        interval,
        intervalUnit: interval_unit,
      })
    }
  }
  return (
    <Popover open={showDateConfig} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{rangeSectionContent}</PopoverTrigger>
      <PopoverContent layout='form' side='top' collisionPadding={8} fitViewport>
        <Text variant='overline' color='muted'>
          <Trans>Quick Select</Trans>
        </Text>
        <PresetGrid>
          {predefinedRanges.map((range) => (
            <Button
              key={range.value}
              type='button'
              variant='outline'
              size='lg'
              pressed='accent'
              aria-pressed={selectedPreset?.value === range.value}
              onClick={() => handlePresetSelect(range)}>
              {range.icon}
              {i18n._(range.label)}
            </Button>
          ))}
        </PresetGrid>
        <CustomRangePicker
          fromTime={fromTime}
          toTime={toTime}
          onApply={handleCustomRangeChange}
        />
      </PopoverContent>
    </Popover>
  )
}
